import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { organizationRepository } from '../repositories/organization.repository';
import { orgKeys } from '../services/organization.keys';
import { useOrganization } from './useOrganization';

export const BUCKET_NAME = 'organization-logos';
export const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
export const ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/svg+xml',
];

/**
 * Extracts storage object path from a Supabase storage URL if it points to the organization-logos bucket.
 * Returns null if the URL is external (e.g. Unsplash, external CDN) or not in this bucket.
 */
export function extractStoragePath(url: string | null | undefined): string | null {
  if (!url) return null;
  const marker = `/${BUCKET_NAME}/`;
  const idx = url.indexOf(marker);
  if (idx !== -1) {
    const rawPath = url.substring(idx + marker.length).split('?')[0];
    return decodeURIComponent(rawPath);
  }
  return null;
}

export function validateLogoFile(file: File): { valid: boolean; error?: string } {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return {
      valid: false,
      error: `File size exceeds 5MB limit. Selected file size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`,
    };
  }

  const fileExt = file.name.split('.').pop()?.toLowerCase();
  const validExts = ['jpg', 'jpeg', 'png', 'webp', 'svg'];
  const isValidExt = fileExt ? validExts.includes(fileExt) : false;
  const isValidMime = ALLOWED_MIME_TYPES.includes(file.type);

  if (!isValidMime && !isValidExt) {
    return {
      valid: false,
      error: 'Invalid file format. Please upload a JPEG, PNG, WEBP, or SVG image.',
    };
  }

  return { valid: true };
}

export interface UseOrganizationLogoOptions {
  onSuccess?: (newUrl: string | null) => void;
  onError?: (error: Error) => void;
}

export function useOrganizationLogo(options?: UseOrganizationLogoOptions) {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const { toast } = useToast();

  const uploadLogoMutation = useMutation({
    mutationFn: async (file: File): Promise<string> => {
      const orgId = currentOrganization?.id;
      if (!orgId) {
        throw new Error('No active organization found to update logo.');
      }

      // 1. Validate file (size <= 5MB, format in JPEG/PNG/WEBP/SVG)
      const validation = validateLogoFile(file);
      if (!validation.valid) {
        throw new Error(validation.error || 'Invalid file');
      }

      const fileExt = file.name.split('.').pop()?.toLowerCase() || 'png';
      const timestamp = Date.now();
      const randomSuffix = Math.random().toString(36).substring(2, 8);
      const newPath = `${orgId}/logo_${timestamp}_${randomSuffix}.${fileExt}`;

      // 2. Upload new file to 'organization-logos'
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(newPath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError || !uploadData) {
        console.error('Storage upload error:', uploadError);
        throw new Error(uploadError?.message || 'Failed to upload logo to storage.');
      }

      // 3. Resolve public URL
      const { data: publicUrlData } = supabase.storage
        .from(BUCKET_NAME)
        .getPublicUrl(uploadData.path);

      const newPublicUrl = publicUrlData.publicUrl;

      // 4. Try updating DB
      const previousLogoUrl = currentOrganization.logo_url;
      try {
        await organizationRepository.updateOrganization(orgId, {
          logo_url: newPublicUrl,
        });
      } catch (dbError) {
        // Rollback: delete newly uploaded file from storage immediately!
        console.error('DB update failed, rolling back uploaded logo asset:', dbError);
        await supabase.storage.from(BUCKET_NAME).remove([uploadData.path]);
        throw dbError;
      }

      // If DB update SUCCEEDED:
      // If previousLogoUrl is in 'organization-logos', delete previous storage object asynchronously
      const oldStoragePath = extractStoragePath(previousLogoUrl);
      if (oldStoragePath && oldStoragePath !== uploadData.path) {
        supabase.storage
          .from(BUCKET_NAME)
          .remove([oldStoragePath])
          .then(({ error: delError }) => {
            if (delError) {
              console.warn('Orphan storage asset cleanup warning:', delError);
            }
          })
          .catch((delErr) => {
            console.warn('Failed to delete previous logo from storage:', delErr);
          });
      }

      return newPublicUrl;
    },
    onSuccess: (newUrl) => {
      queryClient.invalidateQueries({ queryKey: orgKeys.current() });
      queryClient.invalidateQueries({ queryKey: orgKeys.userOrgs() });
      queryClient.invalidateQueries({ queryKey: orgKeys.all });
      toast({
        title: 'Logo Updated',
        description: 'Organization logo has been successfully updated.',
      });
      options?.onSuccess?.(newUrl);
    },
    onError: (error: Error) => {
      console.error('Error in uploadLogoMutation:', error);
      toast({
        title: 'Upload Failed',
        description: error.message || 'Failed to upload organization logo.',
        variant: 'destructive',
      });
      options?.onError?.(error);
    },
  });

  const removeLogoMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      const orgId = currentOrganization?.id;
      if (!orgId) {
        throw new Error('No active organization found.');
      }

      const currentLogoUrl = currentOrganization.logo_url;

      // 1. Update DB FIRST: logo_url = null
      await organizationRepository.updateOrganization(orgId, {
        logo_url: null,
      });

      // 2. If DB update SUCCEEDS:
      // If currentLogoUrl is in 'organization-logos', delete object from storage
      const oldStoragePath = extractStoragePath(currentLogoUrl);
      if (oldStoragePath) {
        const { error: storageError } = await supabase.storage
          .from(BUCKET_NAME)
          .remove([oldStoragePath]);

        if (storageError) {
          // Log warning; user is unaffected since DB points to NULL
          console.warn('Storage deletion warning during logo removal:', storageError);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orgKeys.current() });
      queryClient.invalidateQueries({ queryKey: orgKeys.userOrgs() });
      queryClient.invalidateQueries({ queryKey: orgKeys.all });
      toast({
        title: 'Logo Removed',
        description: 'Organization logo has been removed.',
      });
      options?.onSuccess?.(null);
    },
    onError: (error: Error) => {
      console.error('Error in removeLogoMutation:', error);
      toast({
        title: 'Removal Failed',
        description: error.message || 'Failed to remove organization logo.',
        variant: 'destructive',
      });
      options?.onError?.(error);
    },
  });

  const applyFetchedLogoMutation = useMutation({
    mutationFn: async (url: string): Promise<string> => {
      const orgId = currentOrganization?.id;
      if (!orgId) {
        throw new Error('No active organization found to update logo.');
      }

      let publicUrl = url;

      // Attempt to mirror external/cached logo into the organization-logos bucket for permanent ownership
      try {
        const response = await fetch(url);
        if (response.ok) {
          const blob = await response.blob();
          const mimeType = blob.type || 'image/png';
          const ext = mimeType.split('/')[1]?.replace('svg+xml', 'svg') || 'png';
          const timestamp = Date.now();
          const randomSuffix = Math.random().toString(36).substring(2, 8);
          const newPath = `${orgId}/logo_${timestamp}_${randomSuffix}.${ext}`;

          const { data: uploadData, error: uploadError } = await supabase.storage
            .from(BUCKET_NAME)
            .upload(newPath, blob, {
              contentType: mimeType,
              cacheControl: '3600',
              upsert: false,
            });

          if (!uploadError && uploadData) {
            const { data: publicUrlData } = supabase.storage
              .from(BUCKET_NAME)
              .getPublicUrl(uploadData.path);
            publicUrl = publicUrlData.publicUrl;
          }
        }
      } catch (mirrorErr) {
        console.warn('Could not mirror fetched logo to organization-logos bucket, saving direct URL:', mirrorErr);
      }

      // Update organization in DB
      const previousLogoUrl = currentOrganization.logo_url;
      try {
        await organizationRepository.updateOrganization(orgId, {
          logo_url: publicUrl,
        });
      } catch (dbError) {
        console.error('DB update failed for fetched logo:', dbError);
        throw dbError;
      }

      // Cleanup old storage asset if it was in organization-logos
      const oldStoragePath = extractStoragePath(previousLogoUrl);
      if (oldStoragePath && !publicUrl.includes(oldStoragePath)) {
        supabase.storage
          .from(BUCKET_NAME)
          .remove([oldStoragePath])
          .catch((delErr) => {
            console.warn('Failed to delete previous logo from storage:', delErr);
          });
      }

      return publicUrl;
    },
    onSuccess: (newUrl) => {
      queryClient.invalidateQueries({ queryKey: orgKeys.current() });
      queryClient.invalidateQueries({ queryKey: orgKeys.userOrgs() });
      queryClient.invalidateQueries({ queryKey: orgKeys.all });
      toast({
        title: 'Logo Updated',
        description: 'Organization logo has been successfully updated.',
      });
      options?.onSuccess?.(newUrl);
    },
    onError: (error: Error) => {
      console.error('Error in applyFetchedLogoMutation:', error);
      toast({
        title: 'Update Failed',
        description: error.message || 'Failed to update organization logo.',
        variant: 'destructive',
      });
      options?.onError?.(error);
    },
  });

  return {
    uploadLogo: uploadLogoMutation.mutateAsync,
    removeLogo: removeLogoMutation.mutateAsync,
    applyFetchedLogo: applyFetchedLogoMutation.mutateAsync,
    isUploading: uploadLogoMutation.isPending,
    isRemoving: removeLogoMutation.isPending,
    isApplyingFetchedLogo: applyFetchedLogoMutation.isPending,
    isLoading:
      uploadLogoMutation.isPending ||
      removeLogoMutation.isPending ||
      applyFetchedLogoMutation.isPending,
    uploadError: uploadLogoMutation.error,
    removeError: removeLogoMutation.error,
    applyFetchedLogoError: applyFetchedLogoMutation.error,
  };
}
